import { useEffect, useState } from "react";

/**
 * Recharts needs real colour values, not `var(--viz-1)`, because it writes them
 * into SVG attributes. So we read the resolved tokens off the document and
 * re-read whenever the brand or theme changes.
 *
 * This is the seam between "the design system owns the colours" and "a drawing
 * library needs hex". Nothing else in the chart layer should touch a hex.
 */
export type VizTokens = {
  series: string[];
  sequential: string[];
  diverging: { neg: string; mid: string; pos: string };
  grid: string;
  axis: string;
  label: string;
  text: string;
  surface: string;
  lineWidth: number;
  marker: number;
  barRadius: number;
};

const read = (): VizTokens => {
  if (typeof window === "undefined") {
    return {
      series: [], sequential: [], diverging: { neg: "", mid: "", pos: "" },
      grid: "", axis: "", label: "", text: "", surface: "",
      lineWidth: 2, marker: 8, barRadius: 4,
    };
  }
  const cs = getComputedStyle(document.documentElement);
  const v = (k: string) => cs.getPropertyValue(k).trim();
  const px = (k: string, fallback: number) => parseFloat(v(k)) || fallback;
  return {
    // Fixed order. Never cycled, never reordered, never extended at runtime.
    series: [1, 2, 3, 4, 5, 6, 7, 8].map((i) => v(`--viz-${i}`)),
    sequential: [1, 2, 3, 4, 5].map((i) => v(`--seq-${i}`)),
    diverging: { neg: v("--viz-div-neg"), mid: v("--viz-div-mid"), pos: v("--viz-div-pos") },
    grid: v("--viz-grid"),
    axis: v("--viz-axis"),
    label: v("--viz-label"),
    text: v("--viz-value"),
    surface: v("--viz-surface") || v("--surface"),
    lineWidth: px("--viz-line-w", 2),
    marker: px("--viz-marker", 8),
    barRadius: px("--viz-bar-radius", 4),
  };
};

export function useVizTokens(): VizTokens {
  const [tokens, setTokens] = useState<VizTokens>(read);

  useEffect(() => {
    const update = () => setTokens(read());
    update();
    // Brand or theme swap must repaint the charts.
    const mo = new MutationObserver(update);
    mo.observe(document.documentElement, {
      attributes: true, attributeFilter: ["data-brand", "data-theme"],
    });
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", update);
    return () => { mo.disconnect(); mq.removeEventListener("change", update); };
  }, []);

  return tokens;
}
